/**
 * GPS WebSocket SDK Implementation
 * Provides real-time GPS data communication with FleetA servers
 */

import { GpsWebSocketFactory } from './GpsWebSocketFactory';
import { ConnectionManager } from './ConnectionManager';
import { AuthManager } from './AuthManager';
import { EventManager } from './EventManager';
import { RequestManager } from './RequestManager';
import { GpsConnectionStateMachine } from './GpsConnectionStateMachine';
import { getAuthConfigFromStore, getAuthErrorMessage } from '../auth/authUtils';
import { getGpsWebSocketUrl } from '../api/endpointUtils';
import type {
  GpsWsEvent,
  GpsWsEventHandler,
  GpsConnectionState,
  ActionKey,
  ActionRequestMap,
  ActionResponseMap,
  WsMessage,
  DeviceGpsInfo,
  GpsWebSocketSDKOptions
} from './types';

/**
 * GPS WebSocket SDK Class
 * Facade pattern: Provides simple interface while delegating to specialized managers
 */
export class GpsWebSocketSDK {
  // Injected managers (composition over inheritance)
  private connectionManager: ConnectionManager;
  private authManager: AuthManager;
  private eventManager: EventManager;
  private requestManager: RequestManager;
  private stateMachine: GpsConnectionStateMachine;

  /**
   * Create GPS WebSocket SDK instance
   * WebSocket URL is automatically obtained from API endpoint system
   * @param options - SDK configuration options (optional, uses AuthStore by default)
   */
  constructor(options: Partial<Omit<GpsWebSocketSDKOptions, 'url' | 'token' | 'email'>> = {}) {
    // Get auth config from store
    const authConfig = getAuthConfigFromStore();
    
    if (!authConfig) {
      console.warn('⚠️ [GpsWebSocketSDK] Authentication not configured. Some features may not work.');
    }
    
    // Get WebSocket URL from API endpoint system
    const endpointUrl = getGpsWebSocketUrl();
    
    const fullOptions: GpsWebSocketSDKOptions = {
      url: endpointUrl,
      token: authConfig?.jwtToken || '',
      email: authConfig?.userEmail || '',
      authMethod: 'query',
      maxReconnectAttempts: 5,
      reconnectDelay: 1000,
      requestTimeout: 7000,
      ...options
    };

    // Create factory and components
    const factory = new GpsWebSocketFactory();
    const components = factory.createComponents(fullOptions);
    
    // Assign managers
    this.connectionManager = components.connectionManager;
    this.authManager = components.authManager;
    this.eventManager = components.eventManager;
    this.requestManager = components.requestManager;
    this.stateMachine = components.stateMachine;
    
    // Setup inter-component communication
    this.setupManagerIntegration();
  }

  /**
   * Set JWT authentication token
   * Note: This will override AuthStore token temporarily
   */
  setToken(token: string): void {
    this.authManager.setToken(token);
    this.updateConnectionUrl();
  }

  /**
   * Update authentication from AuthStore
   * Call this when AuthStore auth state changes
   */
  updateAuthFromStore(): void {
    const authConfig = getAuthConfigFromStore();
    
    if (authConfig) {
      this.authManager.setToken(authConfig.jwtToken);
      this.updateConnectionUrl();
      console.log('🔄 [GpsWebSocketSDK] Auth updated from store');
    } else {
      console.warn('⚠️ [GpsWebSocketSDK] No auth config available in store');
    }
  }

  /**
   * Get current JWT token
   */
  getCurrentToken(): string | undefined {
    const managerToken = this.authManager.getToken();
    if (managerToken) {
      return managerToken;
    }
    
    // Fallback to AuthStore
    const authConfig = getAuthConfigFromStore();
    return authConfig?.jwtToken;
  }

  /**
   * Check if token is set
   */
  hasValidToken(): boolean {
    // Check manager token first
    if (this.authManager.hasValidToken()) {
      return true;
    }
    
    // Fallback to AuthStore
    const authConfig = getAuthConfigFromStore();
    return !!(authConfig?.jwtToken && authConfig.jwtToken.length > 0);
  }

  /**
   * Connect to GPS WebSocket server
   * Automatically uses AuthStore authentication
   */
  connect(): void {
    if (!this.hasValidToken()) {
      const errorMessage = getAuthErrorMessage();
      this.eventManager.emit("error", new Error(errorMessage));
      return;
    }

    // Ensure we have latest auth info from store
    if (!this.authManager.hasValidToken()) {
      this.updateAuthFromStore();
    }

    this.connectionManager.connect();
  }

  /**
   * Disconnect from GPS WebSocket server
   */
  disconnect(): void {
    this.connectionManager.disconnect();
  }

  /**
   * Check WebSocket connection status
   */
  isWebSocketConnected(): boolean {
    return this.connectionManager.isConnected();
  }

  /**
   * Get current connection state
   */
  getConnectionState(): GpsConnectionState {
    return this.stateMachine.getState();
  }

  /**
   * Get current WebSocket ready state
   */
  getReadyState(): number | undefined {
    return this.connectionManager.isConnected() ? 1 : 0;
  }

  /**
   * Generic send method with Promise-based response matching
   */
  send<K extends ActionKey>(
    action: K,
    payload: Omit<ActionRequestMap[K], "action" | "sequenceId"> & { sequenceId?: number },
    timeoutMs: number = 7000
  ): Promise<ActionResponseMap[K]> {
    if (!this.isWebSocketConnected()) {
      return Promise.reject(new Error("WebSocket not connected"));
    }

    return this.requestManager.send(action, payload, timeoutMs);
  }

  // ----- Event handling methods -----

  /**
   * Add event listener for GPS WebSocket events
   */
  on<T = any>(event: GpsWsEvent, handler: GpsWsEventHandler<T>): void {
    this.eventManager.on(event, handler);
  }

  /**
   * Remove event listener for GPS WebSocket events
   */
  off(event: GpsWsEvent, handler: GpsWsEventHandler): void {
    this.eventManager.off(event, handler);
  }

  /**
   * Remove all event listeners for a specific event type
   */
  removeAllListeners(event?: GpsWsEvent): void {
    this.eventManager.removeAllListeners(event);
  }

  /**
   * Get statistics about the WebSocket connection
   */
  getConnectionStats(): {
    isConnected: boolean;
    state: GpsConnectionState;
    hasToken: boolean;
    pendingRequests: number;
  } {
    return {
      isConnected: this.isWebSocketConnected(),
      state: this.getConnectionState(),
      hasToken: this.hasValidToken(),
      pendingRequests: this.requestManager.getPendingCount()
    };
  }

  /**
   * Destroy SDK and cleanup all resources
   */
  destroy(): void {
    this.connectionManager.destroy();
    this.authManager.clear();
    this.eventManager.destroy();
    this.requestManager.destroy();
    this.stateMachine.reset();
  }

  // ========== PRIVATE METHODS ==========

  /**
   * Setup integration between managers
   */
  private setupManagerIntegration(): void {
    // Setup connection manager callbacks
    this.connectionManager.setCallbacks({
      onOpen: () => {
        this.stateMachine.transition('CONNECTION_ESTABLISHED');
        
        // Send auth message if needed
        const authMessage = this.authManager.createAuthMessage();
        if (authMessage) {
          this.connectionManager.send(JSON.stringify(authMessage));
        }
        
        this.eventManager.emit("open", undefined);
      },
      onClose: (event) => {
        this.stateMachine.transition('CONNECTION_LOST');
        this.eventManager.emit("close", event);
      },
      onError: (error) => {
        this.stateMachine.transition('FAILURE');
        this.eventManager.emit("error", error);
      },
      onMessage: (event) => {
        this.handleMessage(event);
      },
      onStateChange: (state) => {
        // Connection state changes handled by state machine
      }
    });

    // Setup request manager with connection send function
    this.requestManager.setSendFunction((data: string) => {
      this.connectionManager.send(data);
    });

    // Setup state machine callbacks
    this.stateMachine.setCallbacks({
      onStateChange: (newState, previousState, event) => {
        // Handle state-specific logic if needed
      },
      onInvalidTransition: (currentState, event) => {
        // Handle invalid transitions
      }
    });
  }

  /**
   * Update connection URL based on auth configuration
   */
  private updateConnectionUrl(): void {
    const newUrl = this.authManager.generateConnectionUrl();
    this.connectionManager.updateConfig({ url: newUrl });
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: MessageEvent): void {
    let msg: WsMessage<any>;
    
    try {
      msg = JSON.parse(event.data);
    } catch {
      msg = event.data;
    }

    // Emit raw message event
    this.eventManager.emit("raw", msg);

    // Try to handle as request response first
    const wasHandled = this.requestManager.handleMessage(msg);
    
    if (!wasHandled) {
      // Handle as real-time data
      this.handleRealTimeData(msg);
    }

    // Always emit general message event
    this.eventManager.emit("message", msg);
  }

  /**
   * Handle real-time GPS data
   */
  private handleRealTimeData(msg: WsMessage<any>): void {
    // Emit deviceGps event if device data exists
    if (msg.response?.device_list && Array.isArray(msg.response.device_list)) {
      this.eventManager.emit("deviceGps", msg.response.device_list as DeviceGpsInfo[]);
    } else if (msg.response?.device) {
      this.eventManager.emit("deviceGps", [msg.response.device] as DeviceGpsInfo[]);
    }
  }

  // ----- Action-specific request methods -----

  /**
   * Ensure authentication before API calls
   */
  private ensureAuthenticated(): void {
    if (!this.hasValidToken()) {
      throw new Error(getAuthErrorMessage());
    }
    
    // Ensure we have latest auth info
    if (!this.authManager.hasValidToken()) {
      this.updateAuthFromStore();
    }
  }

  /**
   * Get GPS data for public devices in zone (no authentication required)
   * @param sw - Southwest coordinate (lat,lng)
   * @param ne - Northeast coordinate (lat,lng)
   */
  gpsZoneNoAuth(sw: string, ne: string): Promise<ActionResponseMap['gpsZoneNoAuth']> {
    // Public API - no auth required
    return this.send("gpsZoneNoAuth", { sw, ne });
  }

  /**
   * Get GPS data for user's devices in specified zone
   * @param sw - Southwest coordinate (lat,lng)
   * @param ne - Northeast coordinate (lat,lng)
   */
  gpsZoneMyDevices(sw: string, ne: string): Promise<ActionResponseMap['gpsZoneMyDevices']> {
    this.ensureAuthenticated();
    const authConfig = getAuthConfigFromStore();
    const email = authConfig!.userEmail;
    return this.send("gpsZoneMyDevices", { email, sw, ne });
  }

  /**
   * Get GPS data for all devices in specified zone
   * @param sw - Southwest coordinate (lat,lng)
   * @param ne - Northeast coordinate (lat,lng)
   */
  gpsZoneAllDevices(sw: string, ne: string): Promise<ActionResponseMap['gpsZoneAllDevices']> {
    this.ensureAuthenticated();
    const authConfig = getAuthConfigFromStore();
    const email = authConfig!.userEmail;
    return this.send("gpsZoneAllDevices", { email, sw, ne });
  }

  /**
   * Get GPS data for specific group devices in zone
   * @param sw - Southwest coordinate (lat,lng)
   * @param ne - Northeast coordinate (lat,lng)
   * @param groupIdList - Array of group IDs to filter
   */
  gpsZoneGroupMyDevices(sw: string, ne: string, groupIdList: string[]): Promise<ActionResponseMap['gpsZoneGroupMyDevices']> {
    this.ensureAuthenticated();
    const authConfig = getAuthConfigFromStore();
    const email = authConfig!.userEmail;
    return this.send("gpsZoneGroupMyDevices", { email, sw, ne, groupIdList });
  }

  /**
   * Get GPS data for all devices in specific groups within zone
   * @param sw - Southwest coordinate (lat,lng)
   * @param ne - Northeast coordinate (lat,lng)
   * @param groupIdList - Array of group IDs to filter
   */
  gpsZoneGroupAllDevices(sw: string, ne: string, groupIdList: string[]): Promise<ActionResponseMap['gpsZoneGroupAllDevices']> {
    this.ensureAuthenticated();
    const authConfig = getAuthConfigFromStore();
    const email = authConfig!.userEmail;
    return this.send("gpsZoneGroupAllDevices", { email, sw, ne, groupIdList });
  }

  /**
   * Get GPS data for all user's devices
   */
  gpsPsnsMyDevices(): Promise<ActionResponseMap['gpsPsnsMyDevices']> {
    this.ensureAuthenticated();
    const authConfig = getAuthConfigFromStore();
    const email = authConfig!.userEmail;
    return this.send("gpsPsnsMyDevices", { email });
  }

  /**
   * Get GPS data for a specific device
   * @param psn - Device PSN (Product Serial Number)
   */
  gpsPsnsSpecificDevice(psn: string): Promise<ActionResponseMap['gpsPsnsSpecificDevice']> {
    this.ensureAuthenticated();
    const authConfig = getAuthConfigFromStore();
    const email = authConfig!.userEmail;
    return this.send("gpsPsnsSpecificDevice", { email, psn });
  }

  /**
   * Get GPS data for multiple specific devices (Multi Live View)
   * @param psns - Array of device PSNs
   */
  gpsPsnsMultiLiveView(psns: string[]): Promise<ActionResponseMap['gpsPsnsMultiLiveView']> {
    this.ensureAuthenticated();
    const authConfig = getAuthConfigFromStore();
    const email = authConfig!.userEmail;
    return this.send("gpsPsnsMultiLiveView", { email, psns });
  }
}